import type { User } from "@src/domain/users/entities/user.entity";
import type { UserDTO, UserListItemDTO } from "../dto/user.dto";
import type { RoleDTO } from "../dto/auth.dto";

/**
 * User DTO Mapper
 * Maps between User domain entities and DTOs
 */
export class UserDtoMapper {
    /**
     * Convert User entity to UserDTO
     */
    static toDTO(user: User): UserDTO {
        return {
            user_id: user.userId,
            email: user.email,
            first_name: user.firstName,
            middle_name: user.middleName,
            last_name: user.lastName,
            display_name: user.displayName,
            full_name: user.fullName,
            profile_picture_url: user.profilePictureUrl,
            phone_number: user.phone,
            is_active: user.isActive,
            is_verified: user.isVerified,
            is_banned: user.isBanned,
            created_at: user.createdAt.toISOString(),
            updated_at: user.updatedAt.toISOString(),
            roles: user.roles.map(role => ({
                role_id: role.roleId,
                name: role.name,
                display_name: role.displayName,
                description: role.description,
                is_active: role.isActive,
                permissions: role.permissions,
                created_at: role.createdAt.toISOString(),
                updated_at: role.updatedAt.toISOString(),
            } as RoleDTO)),
            permissions: user.permissions,
        };
    }

    /**
     * Convert User entity to simplified UserListItemDTO
     */
    static toListItemDTO(user: User): UserListItemDTO {
        return {
            user_id: user.userId,
            email: user.email,
            display_name: user.displayName,
            full_name: user.fullName,
            profile_picture_url: user.profilePictureUrl,
            is_active: user.isActive,
            is_verified: user.isVerified,
            is_banned: user.isBanned,
            roles: user.roles.map(role => ({
                role_id: role.roleId,
                name: role.name,
                description: role.description,
                display_name: role.displayName,
            })),
            created_at: user.createdAt.toISOString(),
        };
    }

    /**
     * Convert array of User entities to array of UserListItemDTOs
     */
    static toListItemDTOs(users: User[]): UserListItemDTO[] {
        return users.map(user => this.toListItemDTO(user));
    }
}
